// Top-level application: owns the canvas, the title screen and the current
// game, and wires world persistence (create / load / delete / autosave).

import { Game } from './game';
import { AudioManager } from './render/audio';
import { deleteWorld, listWorlds, loadWorld, saveWorld } from './persist';
import { loadSettings } from './settings';
import { SettingsPanel, TitleScreen } from './ui';

const AUTOSAVE_MS = 30000;

/** Turn a seed string into a 31-bit int (numeric passthrough or string hash). */
function parseSeed(text: string): number {
  if (/^-?\d+$/.test(text)) return Math.abs(Number(text)) % 2 ** 31;
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 1;
}

export class App {
  private canvas: HTMLCanvasElement;
  private audio = new AudioManager();
  private title: TitleScreen;
  private titleSettings: SettingsPanel;
  private game: Game | null = null;
  private autosaveTimer: number | null = null;

  constructor() {
    document.body.style.cssText = 'margin:0;overflow:hidden;background:#000;';
    const app = document.getElementById('app')!;
    app.style.cssText = 'margin:0;width:100vw;height:100vh;overflow:hidden;';
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'display:block;width:100%;height:100%;';
    app.appendChild(this.canvas);

    loadSettings();

    this.titleSettings = new SettingsPanel(
      () => {},
      () => {
        this.titleSettings.hide();
        this.title.show();
      },
    );
    this.title = new TitleScreen({
      onPlayExisting: (name) => this.playExisting(name),
      onCreate: (name, seedText) => this.createWorld(name, seedText),
      onDelete: (name) => this.delete(name),
      onSettings: () => {
        this.title.hide();
        this.titleSettings.show();
      },
    });

    window.addEventListener('beforeunload', () => this.saveCurrent());
  }

  start(): void {
    this.showTitle();
  }

  private showTitle(): void {
    void listWorlds().then((worlds) => {
      this.title.refresh(worlds);
      this.title.show();
    });
  }

  private createWorld(name: string, seedText: string): void {
    const seed = seedText ? parseSeed(seedText) : (Math.random() * 2 ** 31) | 0;
    this.launch(
      new Game(this.canvas, { name, seed, audio: this.audio, onQuit: () => this.quitToTitle() }),
    );
  }

  private playExisting(name: string): void {
    void loadWorld(name).then((saved) => {
      if (!saved) return;
      this.launch(
        new Game(this.canvas, { name, saved, audio: this.audio, onQuit: () => this.quitToTitle() }),
      );
    });
  }

  private launch(game: Game): void {
    this.title.hide();
    this.titleSettings.hide();
    this.game = game;
    game.start();
    this.autosaveTimer = window.setInterval(() => this.saveCurrent(), AUTOSAVE_MS);
  }

  private delete(name: string): void {
    void deleteWorld(name).then(() => this.showTitle());
  }

  private saveCurrent(): void {
    if (this.game) void saveWorld(this.game.getSaveData());
  }

  private quitToTitle(): void {
    if (this.autosaveTimer !== null) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    if (this.game) {
      void saveWorld(this.game.getSaveData());
      this.game.stop();
      this.game = null;
    }
    this.showTitle();
  }
}
